import { useEffect, useState } from "react";
import api, { fmtDateTime } from "../../lib/api";

const TABS = [
  { key: "api", label: "API Logs", endpoint: "/admin/logs/api" },
  { key: "webhooks", label: "Webhook Logs", endpoint: "/admin/logs/webhooks" },
  { key: "audit", label: "Audit Logs", endpoint: "/admin/logs/audit" },
  { key: "routing", label: "Routing & Failover", endpoint: "/admin/routing-logs" },
  { key: "notifications", label: "Notifications", endpoint: "/admin/notifications" },
];

export default function AdminLogs() {
  const [tab, setTab] = useState("api");
  const [rows, setRows] = useState(null);

  useEffect(() => {
    setRows(null);
    api.get(TABS.find((t) => t.key === tab).endpoint).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [tab]);

  return (
    <div data-testid="admin-logs-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Logs & Events</h1>
      <p className="text-sm text-slate-500">Provider API calls, webhooks, failover decisions, notifications and audit trail.</p>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="logs-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`logs-tab-${t.key}`}
            className={`rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${tab === t.key ? "bg-shipany-navy text-white" : "bg-white text-slate-500 border border-slate-200"}`}>{t.label}</button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="logs-table">
        {tab === "api" && (
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white"><th className="px-5 py-3">Provider</th><th className="px-4 py-3">Endpoint</th><th className="px-4 py-3">Method</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Latency</th><th className="px-4 py-3">Error</th><th className="px-4 py-3">Time</th></tr></thead>
            <tbody>
              {(rows || []).map((l) => (
                <tr key={l.log_id} className="border-t border-slate-100">
                  <td className="px-5 py-3 capitalize font-semibold text-shipany-navy">{l.provider_code}</td>
                  <td className="mono px-4 py-3 text-xs">{l.endpoint}</td>
                  <td className="mono px-4 py-3 text-xs">{l.method}</td>
                  <td className="px-4 py-3"><span className={`mono rounded px-2 py-0.5 text-xs font-bold ${l.status < 400 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>{l.status}</span></td>
                  <td className="mono px-4 py-3 text-xs text-shipany-blue">{l.latency_ms}ms</td>
                  <td className="max-w-52 truncate px-4 py-3 text-[11px] text-red-500">{l.error || ""}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">No API calls logged yet.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "webhooks" && (
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white"><th className="px-5 py-3">Provider</th><th className="px-4 py-3">Payload</th><th className="px-4 py-3">Verified</th><th className="px-4 py-3">Received</th></tr></thead>
            <tbody>
              {(rows || []).map((l) => (
                <tr key={l.log_id} className="border-t border-slate-100">
                  <td className="px-5 py-3 capitalize font-semibold text-shipany-navy">{l.provider_code}</td>
                  <td className="mono max-w-md truncate px-4 py-3 text-xs text-slate-500">{JSON.stringify(l.payload)}</td>
                  <td className="px-4 py-3">{l.verified ? <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-600">VERIFIED</span> : <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600">REJECTED</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(l.received_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "audit" && (
          <table className="w-full min-w-[720px] text-sm">
            <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white"><th className="px-5 py-3">Actor</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Entity</th><th className="px-4 py-3">Meta</th><th className="px-4 py-3">Time</th></tr></thead>
            <tbody>
              {(rows || []).map((l) => (
                <tr key={l.log_id} className="border-t border-slate-100">
                  <td className="px-5 py-3 text-xs text-slate-600">{l.actor_email}</td>
                  <td className="px-4 py-3"><span className="rounded bg-shipany-blue/10 px-2 py-0.5 text-xs font-bold text-shipany-blue">{l.action}</span></td>
                  <td className="mono px-4 py-3 text-xs text-slate-500">{l.entity}:{l.entity_id?.slice(-8)}</td>
                  <td className="mono max-w-xs truncate px-4 py-3 text-xs text-slate-400">{JSON.stringify(l.meta)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No audit events yet.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "routing" && (
          <table className="w-full min-w-[820px] text-sm">
            <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white"><th className="px-5 py-3">Order</th><th className="px-4 py-3">Decision Chain</th><th className="px-4 py-3">Time</th></tr></thead>
            <tbody>
              {(rows || []).map((l) => (
                <tr key={l.log_id} className="border-t border-slate-100">
                  <td className="mono px-5 py-3 text-xs font-semibold text-shipany-blue">{l.order_number}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {(l.decisions || []).map((d, i) => (
                        <span key={i} className={`rounded-lg px-2 py-1 text-[11px] font-semibold ${d.result === "success" ? "bg-emerald-50 text-emerald-700" : d.result === "demo_fallback" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>
                          {d.provider_code}/{d.courier} → {d.result}{d.reason ? ` (${d.reason})` : ""}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-slate-400">No routing decisions yet — bookings log here.</td></tr>}
            </tbody>
          </table>
        )}
        {tab === "notifications" && (
          <table className="w-full min-w-[820px] text-sm">
            <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white"><th className="px-5 py-3">Event</th><th className="px-4 py-3">Channel</th><th className="px-4 py-3">To</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Time</th></tr></thead>
            <tbody>
              {(rows || []).map((l) => (
                <tr key={l.log_id} className="border-t border-slate-100">
                  <td className="px-5 py-3"><span className="rounded bg-shipany-cyan/15 px-2 py-0.5 text-xs font-bold uppercase text-shipany-blue">{l.event}</span></td>
                  <td className="px-4 py-3 text-xs font-semibold capitalize text-slate-600">{l.channel}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{l.to}</td>
                  <td className="max-w-md truncate px-4 py-3 text-xs text-slate-500">{l.subject} — {l.body}</td>
                  <td className="px-4 py-3"><span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{l.mode}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No notifications sent yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
